import { FC, Fragment } from "react";
import {
  ComponentMap,
  ComponentTypeNames,
  RichTextMap,
} from "./type-name-mappings";
import { ThemeEnum, getThemeColors } from "./theme";
import ResponsiveGutter from "ui-kit/ResponsiveGutter";
import { HeroTypeEnum } from "./ContentfulHero";
import TrustPilotStars from "components/TrustPilotStars";
import { isOrg } from "utils/site";
import { FlexRow } from "ui-kit/Layout/Flex";
import Colors from "ui-kit/colors";
import { unwrapVariantContainer } from "./ContentfulVariantContainer/resolveVariant";

type ContentfulRichTextRendererProps = {
  component: any;
};

type ContentfulComponentsRendererProps = {
  components: any[];
  gutter?: JSX.Element;
};

type ContentfulLayoutRendererProps = {
  components: any[];
};

const PAGE_HERO_TYPES: HeroTypeEnum[] = [
  HeroTypeEnum.StartpageHero,
  HeroTypeEnum.PuppyCampaignHero,
  HeroTypeEnum.KittenCampaignHero,
  HeroTypeEnum.PetsDeliHero,
  HeroTypeEnum.GermanOOHCampaignHero,
  HeroTypeEnum.FullBackgroundHero,
  HeroTypeEnum.StartpageHeroAll,
  HeroTypeEnum.StartpageHeroCat,
  HeroTypeEnum.StartpageHeroDog,
];

export const firstComponentIsHero = (components: any[]): boolean => {
  const [firstComponent] = components ?? [];
  // An A/B tested hero arrives wrapped in a variant container, so look at the
  // control variant inside it.
  const layoutComponent = unwrapVariantContainer(firstComponent);
  if (
    !layoutComponent ||
    layoutComponent.__typename !== ComponentTypeNames.Hero
  ) {
    return false;
  }
  return PAGE_HERO_TYPES.includes(layoutComponent.type);
};

//swe is currently resctricted due to Kosumentverket
const TrustPilotBanner = () =>
  !isOrg("swe") ? (
    <FlexRow
      justifyContent="center"
      alignItems="center"
      style={{
        background: Colors[getThemeColors(ThemeEnum.Grey)!.backgroundColor],
        height: 50,
      }}
    >
      <TrustPilotStars fullWidth />
    </FlexRow>
  ) : null;

export const ComponentGutter = () => (
  <ResponsiveGutter mobile={17} tablet={18} desktop={19} />
);

export const ContentfulRichTextRenderer: FC<
  ContentfulRichTextRendererProps
> = ({ component }) => {
  const Component = RichTextMap[component.__typename];

  if (!Component) {
    console.warn(`Unknown rich text ${JSON.stringify(component)}`);
    return null;
  }

  return <Component {...component} isRichText />;
};

const getIsNonVisualComponent = (component: any): boolean => {
  return component.__typename === "ComponentStructuredData";
};

export const ContentfulComponentsRenderer: FC<
  ContentfulComponentsRendererProps
> = ({ components, gutter }) => {
  // Carry each rendered node alongside the component it came from, so the
  // gutter logic below can inspect the *actual* preceding rendered component.
  // (Indexing back into `components` breaks once any component renders to null.)
  const rendered = components
    .map((component, i) => {
      const Component = ComponentMap[component.__typename];
      // Gutter/theme decisions run against the control variant of a variant
      // container, so spacing stays identical whichever arm a visitor gets.
      const layoutComponent = unwrapVariantContainer(component);
      const isNonVisualComponent = getIsNonVisualComponent(layoutComponent);

      if (!Component) {
        console.warn(`Unknown component ${JSON.stringify(component)}`);
        return null;
      }

      return {
        node: <Component key={i} {...component} />,
        component: layoutComponent,
      };
    })
    .flatMap((entry) => (entry ? [entry] : []));

  return (
    <>
      {rendered.reduce<JSX.Element[]>((acc, { node }, index) => {
        if (acc.length === 0) {
          return [node];
        }
        // A RichText acting as an intro/heading hugs the component that follows
        // it with a tighter gutter than the standard between-section one. One
        // with its own button (e.g. the contact block) is a section of its own.
        const previous = rendered[index - 1].component;
        const precededByIntroText =
          previous.__typename === ComponentTypeNames.RichText && !previous.c2A;
        return [
          ...acc,
          precededByIntroText ? (
            <ResponsiveGutter
              key={`${index}_gutter`}
              mobile={5}
              tablet={5}
              desktop={5}
            />
          ) : (
            <Fragment key={`${index}_gutter`}>{gutter}</Fragment>
          ),
          node,
        ];
      }, [])}
    </>
  );
};

export const ContentfulLayoutRenderer: FC<ContentfulLayoutRendererProps> = ({
  components,
}) => {
  const [firstComponent] = components;

  if (!firstComponent) {
    return null;
  }

  // A/B tested heroes sit inside a variant container; the hero-first layout
  // checks below read the control variant, while rendering still goes through
  // the container so the assigned variant can swap in on the client.
  const firstLayoutComponent = unwrapVariantContainer(firstComponent);

  //TODO: Remove once new hero is used
  const firstComponentIsOldHero =
    firstLayoutComponent.__typename === ComponentTypeNames.Hero &&
    [
      HeroTypeEnum.StartpageHero,
      HeroTypeEnum.PuppyCampaignHero,
      HeroTypeEnum.KittenCampaignHero,
      HeroTypeEnum.PetsDeliHero,
      HeroTypeEnum.GermanOOHCampaignHero,
      HeroTypeEnum.FullBackgroundHero,
    ].includes(firstLayoutComponent.type);

  const firstComponentIsNewHero =
    firstLayoutComponent.__typename === ComponentTypeNames.Hero &&
    [
      HeroTypeEnum.StartpageHeroAll,
      HeroTypeEnum.StartpageHeroCat,
      HeroTypeEnum.StartpageHeroDog,
    ].includes(firstLayoutComponent.type);

  return (
    <>
      {firstComponentIsOldHero && (
        <>
          <ContentfulComponentsRenderer components={[firstComponent]} />
          <ResponsiveGutter mobile={6} tablet={7} desktop={10} />
        </>
      )}
      {firstComponentIsNewHero && (
        <>
          <ContentfulComponentsRenderer
            components={[firstComponent]}
            gutter={<ComponentGutter />}
          />
          <TrustPilotBanner />
          <ResponsiveGutter mobile={6} tablet={7} desktop={10} />
        </>
      )}
      {/* Pages that don't open with a hero have no top spacer, so the first
          component butts against the sticky nav. Add the same spacer-md the
          hero pages get, so text-first pages breathe below the nav. */}
      {!firstComponentIsOldHero && !firstComponentIsNewHero && (
        <ResponsiveGutter mobile={6} tablet={7} desktop={10} />
      )}
      <ContentfulComponentsRenderer
        components={
          firstComponentIsOldHero || firstComponentIsNewHero
            ? components.slice(1)
            : components
        }
        gutter={<ComponentGutter />}
      />
    </>
  );
};
